import { isNaN, without } from 'lodash';

import { SankeyFlow } from './types';

export const newFlowId = (rowIndex: number) => `flow-${rowIndex}`;

/*
 * Construct a human-readable version of the flow for error messages
 */
export const flowInHumanFormat = (f: SankeyFlow) =>
  isNaN(f.amount)
    ? `| ${f.source.name} | ${f.target.name} |`
    : isNaN(f.amountComparison)
      ? `| ${f.source.name} | ${f.target.name} | ${f.amount} |`
      : `| ${f.source.name} | ${f.target.name} | ${f.amount} | ${f.amountComparison} |`;

/*
 * This method repeatedly finds cycles in the given list of flows, removes
 * one edge from each cycle, and returns all found cycles and removed edges.
 * NOTE: This method has been generated and explained with GitHub Copilot.
 */
export const findFlowCycles = (flows: SankeyFormFlow[]): FlowCycle[] => {
  const visited = new Set<string>();
  const recStack = new Set<string>();
  const cycles: FlowCycle[] = [];

  // Converts flows to a graph representation, basically a
  // map of source nodes to the list of their target nodes.
  const graph = flows.reduce(
    (acc, flow) => {
      acc[flow.source] ||= [];
      acc[flow.source].push(flow.target);
      return acc;
    },
    {} as { [node: string]: string[] }
  );

  // Depth-First Search (DFS) is a graph traversal algorithm used here to detect cycles.
  // It starts from a given node and explores as far as possible along each branch before backtracking.
  // The function keeps track of visited nodes to avoid reprocessing and uses a recursion stack to detect cycles.
  // If a node is revisited while still in the recursion stack, a cycle is detected.
  // The detected cycle is then recorded with its human-readable path and the back flow causing the cycle.
  // Also, the back flow is removed from the graph to break the cycle and continue the search.
  function dfs(node: string, path: string[]): boolean {
    if (!visited.has(node)) {
      visited.add(node);
      recStack.add(node);
      path.push(node);

      for (const neighbor of graph[node] || []) {
        if (!visited.has(neighbor)) {
          if (dfs(neighbor, [...path])) {
            return true;
          }
        } else if (recStack.has(neighbor)) {
          // Cycle detected: clip the path to the true cycle and record it,
          // marking the last edge in its path as the back flow. Also remove the
          // back flow from the graph to break the cycle and continue the search.
          const cycleStart = path.findIndex((n) => n === neighbor);
          cycles.push({
            humanizedPath: [...path.slice(cycleStart), neighbor].join(' > '),
            backFlow: { source: node, target: neighbor },
          });
          graph[node] = without(graph[node], neighbor);
          return true;
        }
      }
    }
    recStack.delete(node);
    return false;
  }

  // The findAndRemoveCycle function iterates over all nodes in the graph to find and remove cycles.
  // It clears the visited and recursion stack sets before starting the search.
  // For each unvisited node, it calls the DFS function to detect cycles.
  // When a cycle is detected, the function removes the back edge of the cycle from the graph to break the cycle.
  // This process is repeated until no more cycles are found in the graph.
  // The function returns true if a cycle was found and removed, and false otherwise.
  function findAndRemoveCycle(): boolean {
    visited.clear();
    recStack.clear();
    for (const node in graph) {
      if (!visited.has(node) && dfs(node, [])) {
        return true;
      }
    }
    return false;
  }

  while (findAndRemoveCycle()) {}

  return cycles;
};
